import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  getStoredReferralCode,
  clearStoredReferralCode,
} from "@/services/referralApi";

function sendRegistration(mail, password, tnc, referralCode, spamCheck, callbacks) {
  const url =
    process.env.REACT_APP_SOPHYAPP_API_URL + "/de/react/0000/account.json";
  var params = new FormData();
  params.append("account[email]", mail);
  params.append("account[password]", password);
  params.append("account[tnc]", tnc === true ? 1 : 0);
  if (referralCode) {
    params.append("account[referral_code]", referralCode);
  }
  // Checked by SignupProtection on the server: the honeypot fields must stay
  // empty, and fill_ms is how long the form was open before it was sent.
  params.append("name_sophyapp", spamCheck.honeypotName);
  params.append("address_sophyapp", spamCheck.honeypotAddress);
  params.append("fill_ms", spamCheck.fillMs);
  axios
    .post(url, params)
    .then(function (response) {
      // An unknown code does not stop the sign-up; it can be entered again
      // in the payment dialog before the first order.
      if (referralCode && response.data && response.data.referralApplied === false) {
        callbacks.setReferralRejected(true);
      }
      clearStoredReferralCode();
      callbacks.setRegistrationSuccess(true);
    })
    .catch(function () {
      callbacks.setRegistrationFailed(true);
    });
}

function NewRegistrationModal({
  setRegistrationSuccess,
  setRegistrationFailed,
  setReferralRejected,
  ...props
}) {
  const [mail, setMail] = useState("");
  const [password, setPassword] = useState("");
  const [pwRepeat, setPwRepeat] = useState("");
  const [tnc, setTnc] = useState(false);
  const [referralCode, setReferralCode] = useState(getStoredReferralCode);
  const [honeypotName, setHoneypotName] = useState("");
  const [honeypotAddress, setHoneypotAddress] = useState("");
  const openedAt = useRef(Date.now());
  const { t } = useTranslation();

  // The modal stays mounted while hidden, so time from each opening.
  useEffect(() => {
    if (props.show) openedAt.current = Date.now();
  }, [props.show]);

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (password === pwRepeat) {
      const spamCheck = {
        honeypotName,
        honeypotAddress,
        fillMs: Date.now() - openedAt.current,
      };
      sendRegistration(mail, password, tnc, referralCode.trim(), spamCheck, {
        setRegistrationSuccess,
        setRegistrationFailed,
        setReferralRejected: setReferralRejected || (() => {}),
      });
      props.onHide();
    } else {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <Modal
      {...props}
      className="lv2-auth-modal"
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t("user.therapist-registration-title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>{t("profile.email")}</Form.Label>
            <Form.Control
              type="email"
              placeholder="user@patient.com"
              autoFocus
              pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
              required
              onChange={(e) => setMail(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>{t("general.passwordLength")}</Form.Label>
            <Form.Control
              type="password"
              pattern=".{8,20}"
              placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>{t("general.repeatPassword")}</Form.Label>
            <Form.Control
              type="password"
              pattern=".{8,20}"
              placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
              onChange={(e) => setPwRepeat(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="referralCode">
            <Form.Label>{t("referral.registrationLabel")}</Form.Label>
            <Form.Control
              type="text"
              placeholder="sophy-xxxxx"
              autoComplete="off"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
            />
            <Form.Text muted>{t("referral.registrationHint")}</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="tnc">
            <Form.Check
              type="checkbox"
              label={
                <span>
                  Ich akzeptiere die Vereinbarung{" "}
                  <a
                    href="https://www.hoferdigital.at/impressum-datenschutz/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("misc.common.terms")}
                  </a>{" "}
                  und{" "}
                  <a
                    href="https://app.sophyapp.com/users/generate_pdf/Ihr%20Unternehmen.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {" "}
                    Datenschutzbestimmungen{" "}
                  </a>
                </span>
              }
              checked={tnc}
              onChange={(e) => setTnc(e.target.checked)}
            />
          </Form.Group>
          {/* Honeypot for form-filling bots. Clipped off screen rather than
              display:none, which bots skip. The names avoid anything browser
              autofill recognises (name, address, ...), so people never fill it. */}
          <div className="visually-hidden" aria-hidden="true">
            <input
              type="text"
              name="sp_reference"
              tabIndex={-1}
              autoComplete="off"
              value={honeypotName}
              onChange={(e) => setHoneypotName(e.target.value)}
            />
            <input
              type="text"
              name="sp_note"
              tabIndex={-1}
              autoComplete="off"
              value={honeypotAddress}
              onChange={(e) => setHoneypotAddress(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="bs-button-color" type="submit" disabled={!tnc}>
            {t("mails.send")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default NewRegistrationModal;